#!/usr/bin/env node
import { InvalidArgumentError, Option, program } from 'commander';
import { render } from 'ink';
import { FC, createElement } from 'react';
import 'source-map-support/register';

import BuildCommand from './commands/build/BuildCommand';
import BuildsCommand from './commands/BuildsCommand';
import IntrospectCommand from './commands/introspect/IntrospectCommand';
import LogoutCommand from './commands/LogoutCommand';
import ReleaseCommand from './commands/release/ReleaseCommand';
import SmokeTestCommand from './commands/smoke-test/SmokeTestCommand';
import WhoamiCommand from './commands/WhoamiCommand';
import CliError from './utilities/CliError';
import getCliVersion from './utilities/getCliVersion';
import onCommand from './utilities/onCommand';
import { setConfigDir, setEphemeralMode } from './utilities/configStore';
import {
  BreakpointResolution,
  formatBreakpointCatalog,
  resolveBreakpointInput,
} from './commands/build/utilities/breakpoints';
import {
  continueBreakpoint,
  isValidPlatform,
} from './commands/build/utilities/continueBreakpoint';
import { PlatformName } from '@todesktop/shared';

function parseCount(value: string): number {
  const parsedValue = Number.parseInt(value, 10);
  if (parsedValue > 0) {
    return parsedValue;
  }

  throw new InvalidArgumentError('Should be a positive number');
}

const configOption = new Option(
  '--config [string]',
  'Path to a different configuration file. If not specified, ' +
    '`todesktop.json` in the project root will be used',
).argParser((value?: string): string | undefined => {
  if (typeof value === 'string' && value === '') {
    throw new InvalidArgumentError('Provided config path is empty');
  }

  return value;
});

program
  .name('todesktop')
  .version(getCliVersion())
  .option(
    '--config-dir <path>',
    'Custom directory for storing CLI configuration and credentials. ' +
      'Can also be set via TODESKTOP_CONFIG_DIR environment variable',
  )
  .option(
    '--ephemeral',
    'Do not persist any configuration or credentials to disk. ' +
      'Useful for CI environments where credentials are provided via environment variables',
  )
  .hook('preAction', (thisCommand) => {
    const opts = thisCommand.opts();
    if (opts.configDir) {
      setConfigDir(opts.configDir);
    }
    if (opts.ephemeral) {
      setEphemeralMode(true);
    }
  });

program
  .command('build')
  .description(
    'Build an Electron app with native installers, code signing baked-in, ' +
      "etc. but without releasing it (existing users won't get an " +
      'auto-update). For quicker builds, you can append `--code-sign=false` ' +
      'to disable code-signing and notarization.',
  )
  .option(
    '--code-sign [bool]',
    'Whether or not code-signing and notarization should be performed. ' +
      'Disable this for quicker builds',
  )
  .option(
    '--async',
    'Upload your app for building on our servers but exit after the upload ' +
      'is complete. Do not wait until the app is actually built',
  )
  .option(
    '--webhook [string]',
    'Send POST request to the webhook URL after building is finished',
  )
  .option(
    '--ignore-extends-errors [bool]',
    'Ignore `id` and `appId` validation errors when extending another todesktop.json file',
  )
  .option('--introspect', 'Enable interactive debugging for this build')
  .option(
    '--breakpoints <list>',
    'Comma-separated list of build breakpoints (requires --introspect). Use `--breakpoints=list` to show supported names',
  )
  .option(
    '--continue <buildId>',
    'Resume a paused introspection breakpoint for an existing build',
  )
  .option(
    '--platform <platform>',
    'Platform to target when using --continue (mac, windows, linux)',
  )
  .addOption(configOption)
  .action(
    async ({
      async,
      codeSign,
      config,
      webhook,
      ignoreExtendsErrors,
      introspect,
      breakpoints,
      continue: continueBuildId,
      platform,
    }) => {
      if (continueBuildId) {
        if (!platform) {
          console.error(
            '--continue requires a valid --platform option (mac, windows, linux).',
          );
          process.exit(1);
        }

        if (!isValidPlatform(platform)) {
          console.error(
            '--continue requires a valid --platform option (mac, windows, linux).',
          );
          process.exit(1);
        }

        try {
          const resolvedPlatform: PlatformName = platform;
          await continueBreakpoint({
            buildId: continueBuildId,
            platform: resolvedPlatform,
            configPath: config,
            ignoreExtendsErrors: ignoreExtendsErrors === true,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          console.error(message);
          process.exit(1);
        }

        return;
      }

      if (platform) {
        console.error('--platform is only valid when used with --continue');
        process.exit(1);
      }

      if (breakpoints === 'list') {
        console.info(formatBreakpointCatalog());
        return;
      }

      let breakpointPlan: BreakpointResolution | undefined;
      if (typeof breakpoints === 'string') {
        if (introspect !== true) {
          console.error(
            '--breakpoints requires --introspect. Re-run with --introspect to enable pause points.',
          );
          process.exit(1);
        }

        const resolution = resolveBreakpointInput(breakpoints);
        if ('error' in resolution) {
          console.error(resolution.error);
          process.exit(1);
        }
        breakpointPlan = resolution;
      }

      runCommand(BuildCommand, {
        configPath: config,
        flags: {
          exitAfterUploading: async === true,
          shouldCodeSign: codeSign !== 'false',
          ignoreExtendsErrors: ignoreExtendsErrors === true,
          onBuildFinishedWebhook: webhook,
          introspect: introspect === true,
          breakpointPlan,
        },
      });
    },
  );

program
  .command('builds')
  .description('View your builds')
  .argument('[id]', 'View a specific build by ID')
  .option('--latest', 'View the latest build')
  .addOption(configOption)
  .option('--count [number]', 'Number of builds to show per page', parseCount)
  .addOption(
    new Option('--format <type>', 'Format to output the build details in')
      .choices(['json', 'table'])
      .default('table'),
  )
  .option(
    '--exit',
    'Disable dynamic pagination and exit the process once the build data ' +
      'has been displayed',
  )
  .action((id, { config, count, exit, format, latest }) => {
    runCommand(BuildsCommand, {
      configPath: config,
      count,
      exit,
      format,
      id,
      shouldViewLatest: latest,
    });
  });

program
  .command('logout')
  .description('Logs you out')
  .action(() => {
    runCommand(LogoutCommand, null, { exitIfOutOfDate: false });
  });

program
  .command('release')
  .description('Release a build')
  .argument('[id]', 'A specific build ID to release')
  .option('--force', 'Skips interactive confirmation step')
  .option('--latest', 'Release the latest build')
  .addOption(configOption)
  .action((id, { config, force, latest }) => {
    runCommand(ReleaseCommand, {
      configPath: config,
      shouldConfirm: !force,
      buildId: latest ? 'latest' : id,
    });
  });

program
  .command('smoke-test')
  .description('Check whether the build works and can be successfully updated')
  .argument('[id]', 'A specific build ID to test')
  .option('--latest', 'Release the latest build')
  .addOption(configOption)
  .action((id, { config, latest }) => {
    runCommand(SmokeTestCommand, {
      configPath: config,
      buildId: latest ? 'latest' : id,
    });
  });

program
  .command('introspect [buildId]')
  .description('Connect to an ongoing build for interactive debugging')
  .option(
    '--platform <platform>',
    'Platform to introspect (mac, windows, linux)',
  )
  .option('--latest', 'Connect to the latest build with introspection enabled')
  .option('--verbose', 'Show detailed connection information')
  .addOption(configOption)
  .action(
    (
      buildId?: string,
      options?: {
        platform?: string;
        latest?: boolean;
        verbose?: boolean;
        config?: string;
      },
    ) => {
      runCommand(IntrospectCommand, {
        buildId,
        platform: options?.platform,
        verbose: options?.verbose,
        latest: options?.latest,
        configPath: options?.config,
      });
    },
  );

program
  .command('whoami')
  .description("Prints the email of the account you're signed into")
  .action(() => {
    runCommand(WhoamiCommand);
  });

const runCommand = <T extends FC<Props>, Props extends object>(
  component: T,
  props: Props | null = null,
  { exitIfOutOfDate = true } = {},
) => {
  onCommand({ exitIfOutOfDate });

  const { waitUntilExit } = render(createElement(component, props));

  waitUntilExit().catch((error) => {
    console.error(CliError.from(error).message);
  });
};

// Don't exist with status 1 if no command specified
const args = [...process.argv];
if (args.length === 2) {
  args.push('help');
}

program.parse(args);
